import React, { useEffect, useState } from 'react';
import { WidgetRenderer } from 'widget-renderer';
import { Footer } from 'footer';
import PropTypes from 'prop-types';
import './scss/login-renderer-overrides.scss';
import './scss/login-renderer.scss';
import { LoadingState } from '@fluid/loading-ui';
import { GenericError } from '@fluid/callouts';
import * as AnomalyDetection from 'common-ui/src/anomaly-detection';
import useLoginFetch from './src/uc-login/useLoginFetch';

import {
    getSupportedLocales,
    initPage,
    handleUserLockedState,
} from './src/uc-login/Utils';
import { selectLink, submitForm } from './src/uc-login/ActionUtils';

const WIDGET_RENDERER_MESSAGES = {
    loadErrorTitle: 'Error',
    loadErrorMessage: 'Error loading widget',
};
const renderError = () => {
    return (
        <GenericError
            msg="Please try your request again later. If this issue persists, please contact our support desk."
            title="Something Went Wrong."
        />
    );
};
const LoginWidgetRenderer = (props) => {
    const {
        footerConfig,
        legacyFooterConfig,
        config,
        onLoginSubmit,
        navigationData,
        onLocaleSelect,
        isSubmittingLogin,
        isLegacy,
        ibguid,
    } = props;

    const [supportedLocales, setSupportedLocales] = useState([]);
    const [isLoadingMessages, setLoadingMessages] = useState(false);
    const { locale: localeParam = 'en_US' } = config;
    const locale = localeParam || 'en_US';

    const { response, isLoading, error } = useLoginFetch({
        methodName: 'getLoginConfig',
    });

    const retail = response?.retail;
    const fdicConfiguration = response?.fdicConfiguration;
    const loginWidgetConfig = retail?.pages?.[0];

    useEffect(() => {
        if (ibguid) {
            const { location } = window;
            const { timeout, logout } = config;
            const { href } = location ?? {};
            let reason = timeout;

            if (!reason) {
                reason = logout ?? '';
            }

            if (href && href.indexOf('ibguid') === -1) {
                const sep = href.indexOf('?') === -1 ? '?' : '&';
                const newURL = `${href}${sep}reason=${reason}&ibguid=${ibguid}`;
                window.history.replaceState(undefined, '', newURL);
            }
        }
    }, []);

    useEffect(() => {
        setSupportedLocales(getSupportedLocales(navigationData, locale));
    }, [navigationData, locale]);

    useEffect(initPage(config, isLegacy), []);

    const handleWidgetInit = () => {
        const adConfig = {
            journeyType: AnomalyDetection.JourneyType.Login,
            subpageType: AnomalyDetection.SubpageType.Login,
            trigger: { '#login-form': 'submit' },
            isPortScanningEnabled: true,
        };

        AnomalyDetection.startJourney(adConfig);
    };

    useEffect(
        handleUserLockedState(
            config,
            loginWidgetConfig,
            setLoadingMessages,
            isLegacy,
        ),
        [config.error, config.userLocked, loginWidgetConfig],
    );

    React.useEffect(() => {
        return () => {
            // Stop callsign journey
            AnomalyDetection.stopJourney();
        };
    }, []);

    if (isLoading || isLoadingMessages) {
        return <LoadingState />;
    }

    if (error) {
        return renderError();
    }

    if (!loginWidgetConfig) {
        return renderError();
    }
    const theme = retail?.theme;
    const { sourceUrl } = loginWidgetConfig ?? {};

    const handleSubmit = (e) => {
        submitForm(config, e, isLegacy, onLoginSubmit);
    };

    const handleLocaleSelect = (selectedLocale) => {
        if (onLocaleSelect) {
            onLocaleSelect(selectedLocale);
        }
    };

    const handleLinkSelect = (e) => {
        selectLink(e, config, isLegacy);
    };

    return (
        <React.Fragment>
            {isSubmittingLogin && (
                <div className="login-renderer-overlay">
                    <LoadingState />
                </div>
            )}

            <WidgetRenderer
                containerCls="dbk-login-web"
                messages={WIDGET_RENDERER_MESSAGES}
                extraWidgetProperties={{
                    locale,
                    legacyLoginConfig: config,
                    supportedLocales,
                    loginConfig: loginWidgetConfig,
                    fdicConfig: fdicConfiguration,
                    theme,
                    uiConfig: {
                        experienceServiceUIBaseUrl:
                            config.experienceServiceUIBaseUrl,
                        olbUIBaseUrl: process.env.REACT_APP_CONTEXT,
                    },

                    onLocaleSelect: handleLocaleSelect,
                    onLinkSelect: handleLinkSelect,
                    onLoginSubmit: handleSubmit,
                    onWidgetInit: handleWidgetInit,
                }}
                sourceUrl={sourceUrl}
            />

            {isLegacy && (
                <Footer
                    footerConfig={footerConfig}
                    legacyFooterConfig={legacyFooterConfig}
                />
            )}
        </React.Fragment>
    );
};

LoginWidgetRenderer.propTypes = {
    footerConfig: PropTypes.instanceOf(Object),
    legacyFooterConfig: PropTypes.instanceOf(Object),
    config: PropTypes.instanceOf(Object),
    navigationData: PropTypes.instanceOf(Object),
    isLegacy: PropTypes.bool,
    isSubmittingLogin: PropTypes.bool,
    onLoginSubmit: PropTypes.func,
    onLocaleSelect: PropTypes.func,
    ibguid: PropTypes.string,
};

export default LoginWidgetRenderer;
